/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import forEach from 'lodash/forEach'
import React from 'react'

import useIntegration from '@/hooks/useIntegration'
import { Form, Page, Question } from '@/types'
import { Row } from '@/types/row'
import { Section } from '@/types/section'

import { QUESTION_FOR_FORM_BUILDER } from '../../constants'
import useQuestionIntegration from './useQuestionIntegration'

export function useFormBuilderJsonParser() {
  const { makeQuestionIntegration } = useQuestionIntegration()
  const { integration } = useIntegration()

  const parseFormFromJson = React.useCallback(
    (json: Form) => {
      const questions: Record<string, Question> = {}
      const pages: Record<string, Page> = {}
      const sections: Record<string, Section> = {}
      const rows: Record<string, Row> = {}

      const parseQuestions = (row: Row) => {
        forEach(row.questions, (question: Question) => {
          let question_question_type = question.question_type

          if (question?.question_type?.slug === 'signature-signature') {
            question_question_type = {
              is_office_use_only: question?.question_type?.is_office_use_only,
              question_type: 'signature',
              question_subtype: 'all',
              slug: 'signature-all',
            }
          }

          const { question_type, question_subtype } = question_question_type

          const idForBuilder = Object.keys(QUESTION_FOR_FORM_BUILDER)?.find(
            (key) => {
              const question_for_form_builder = {
                // @ts-ignore
                ...QUESTION_FOR_FORM_BUILDER[key],
              }
              question_for_form_builder.subtypes =
                question_for_form_builder.subtypes.map(
                  (subtype: any) => subtype.id
                )

              return (
                question_for_form_builder.id === question_type &&
                question_for_form_builder.subtypes.includes(question_subtype)
              )
            }
          )

          const questionId = question.id.toString()

          // @ts-ignore
          questions[questionId] = {
            ...question,
            question_type: question_question_type,
            question_rows: question.question_rows.map((row) => ({
              ...row,
              text: row.text || '',
            })),
            question_choices: question.question_choices.map((choice) => ({
              ...choice,
              text: choice.text || '',
            })),
            question_columns: question.question_columns.map((column) => ({
              ...column,
              text: column.text || '',
            })),
            id: questionId,
            imageToDisplayOnBuilder: question.image,
            row: row.id,
            section: row.section,
            loadedFromServer: true,
            id_for_builder: idForBuilder ?? question_type,
            demographics: question.demographics,
            // @ts-ignore
            athena_integration: question.athena_integration
              ? makeQuestionIntegration({ ...question.athena_integration })
              : null,
            // @ts-ignore
            chirotouch_integration: question.chirotouch_integration
              ? makeQuestionIntegration({ ...question.chirotouch_integration })
              : null,
            // @ts-ignore
            elation_integration: question.elation_integration
              ? makeQuestionIntegration({ ...question.elation_integration })
              : null,
            // @ts-ignore
            open_dental_integration: question.open_dental_integration
              ? makeQuestionIntegration({ ...question.open_dental_integration })
              : null,
          }
          // @ts-ignore
          delete questions[questionId].image
        })
      }

      const parseRows = (section: Section) => {
        forEach(section.rows, (row: Row) => {
          parseQuestions(row)
          rows[row.id] = { ...row }
          // @ts-ignore
          delete rows[row.id].questions
        })
      }

      const parseSections = (page: Page) => {
        forEach(page.sections, (section: Section) => {
          parseRows(section)
          sections[section.id] = { ...section }
          delete sections[section.id].questions
        })
      }

      const parsePages = () => {
        forEach(json.pages, (page) => {
          parseSections(page)
          pages[page.id] = { ...page }
          // @ts-ignore
          delete pages[page.id].sections
        })
      }

      const formName = json.name
      const formTextName = json.text
      const formId = json.id
      const formReportType =
        // @ts-ignore
        json[
          integration
            ? `${integration}_pdf_report_type`
            : 'elation_pdf_report_type'
        ]
      const formDownloadable = json.downloadable
      const internalForm = json.is_internal_form
      const autoSignOffEnabled = json.auto_sign_off
      const elationPdfNonFax = json.elation_pdf_non_fax
      const notificationUsers = json.notification_users
      const redirectUrl = json.redirect_url
      const customCompletePage = json.custom_complete_page
      const hasRedirectRules = json.has_redirect_rules
      const measuresEnabledForm = json.measure_enabled
      parsePages()

      return {
        autoSignOffEnabled,
        elationPdfNonFax,
        formDownloadable,
        formId,
        formName,
        formReportType,
        formTextName,
        internalForm,
        notificationUsers,
        pages,
        questions,
        redirectUrl,
        customCompletePage,
        hasRedirectRules,
        rows,
        sections,
        measuresEnabledForm,
      }
    },
    [integration, makeQuestionIntegration]
  )

  return {
    parseFormFromJson,
  }
}
